import { useMemo } from "react";
import { useDeployment, useNodes } from "../../api/queries";
import type { Deployment } from "../../api/types";
import { EnvironmentBadge } from "../../components/EnvironmentBadge";
import { DeploymentPlacementStatusPill } from "../../components/StatePill";
import { useEnvironmentsById } from "../environments/useEnvironmentsById";
import { formatRelativeTime, shortDigest } from "./deployFormat";
import "./services.css";

interface DeployInFlightCardProps {
  projectId: string;
  serviceId: string;
  /** The service's active (non-terminal) deployment — `"pending"` or `"deploying"`. Callers
   * only render this card while such a deployment exists (see `ServiceDetailPage`). */
  active: Deployment;
}

/**
 * The ServiceRollout board's "deploy in flight" card (IA slice 4) — the old→new revision
 * line, the `haltOnly`-derived auto-rollback statement, and the per-placement convergence
 * states, all read straight off `Deployment`/`DeploymentDetail` (A-5 — nothing here is a
 * second, hand-set copy of what `useDeployment` already polls live while the rollout is
 * active). Two things the RollbackRisk/ServiceRollout mocks show that this deliberately
 * OMITS, both A-10 groundedness calls made for this slice:
 *
 * - the "signed-by-ci ✓" provenance badge — `Deployment` carries no provenance/signature
 *   field today (that vocabulary exists only for the CORPUS provenance ladder, ADR-0053 §2,
 *   an unrelated concept); showing a badge with nothing behind it would be a lying surface.
 * - the mock's per-placement "healthy 3/3" / "40s" container-level health-check counters —
 *   `DeploymentPlacementOutcome` carries `status`/`digest`/`error`/`warnings`, not a
 *   container health-check tally or elapsed time; this card renders what IS real (the
 *   status pill, node, env, digest, error/warnings) rather than fabricating the rest.
 */
export function DeployInFlightCard({ projectId, serviceId, active }: DeployInFlightCardProps) {
  const { data: detail } = useDeployment(projectId, serviceId, active.id);
  const { data: nodesPage } = useNodes({ limit: 100 });
  const nodesById = useMemo(() => new Map(nodesPage?.items.map((n) => [n.id, n])), [nodesPage]);
  const environmentsById = useEnvironmentsById();

  const placements = detail?.placements ?? [];

  return (
    <section className="deploy-in-flight" aria-labelledby="deploy-in-flight-heading">
      <div className="deploy-in-flight__header">
        <div id="deploy-in-flight-heading" className="deploy-in-flight__eyebrow">
          {/* The deployment's opaque id is an internal identifier, not something an
              operator reads at a glance (S-15) — the digest (or image, its next-best
              stand-in) is the thing this deploy is actually identified by. */}
          Deploy in flight — {active.digest ? shortDigest(active.digest) : active.image || "unpinned image"} ·
          started{" "}
          <time dateTime={active.createdAt} title={new Date(active.createdAt).toLocaleString()}>
            {formatRelativeTime(active.createdAt)}
          </time>{" "}
          by {active.createdBy || "—"}
        </div>
        {/* `haltOnly` is the one real field behind the "halts on failure" copy (ADR-0031
            §2b) — a halted rolling deploy either attempts auto-rollback or doesn't,
            never both, so this always renders the ACCURATE half of the mock's static
            "halts on failure · auto-rollback" line, never a blanket claim. */}
        <span className="deploy-in-flight__policy">
          {active.haltOnly
            ? "halts on failure — auto-rollback is off for this deploy"
            : "halts on failure · auto-rollback to the prior running revision"}
        </span>
      </div>

      <div className="deploy-in-flight__revision data-table__mono">
        {active.previousImage && (
          <>
            <span className="deploy-in-flight__revision-from">{shortDigest(active.previousImage)} (running)</span>
            <span aria-hidden="true"> → </span>
          </>
        )}
        <span className="deploy-in-flight__revision-to">
          {active.digest ? shortDigest(active.digest) : active.image || `deployment ${active.id}`} (new)
        </span>
      </div>

      {placements.length === 0 ? (
        <p className="form-field__hint">Rolling out — no machine has reported yet.</p>
      ) : (
        <div className="deploy-in-flight__placements">
          {placements.map((p) => {
            const node = nodesById.get(p.nodeId);
            return (
              <div key={p.placementId} className="deploy-in-flight__placement">
                <DeploymentPlacementStatusPill status={p.status} />
                <span className="deploy-in-flight__placement-node">
                  {node?.environmentName && (
                    <EnvironmentBadge name={node.environmentName} envClass={environmentsById.get(node.environmentId)?.class} />
                  )}
                  <span className="data-table__mono" title={p.nodeId}>
                    {node?.name ?? p.nodeId}
                  </span>
                </span>
                <span className="deploy-in-flight__placement-detail data-table__secondary">
                  {p.error || (p.digest ? shortDigest(p.digest) : "—")}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
